import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Immutable } from "immer";
import { useAtomValue } from "jotai";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import invariant from "tiny-invariant";

import {
    CmsBlock,
    CmsColumnBlock,
    CmsContainerBlock,
    CmsFeaturedStreamingChannelBlock,
    CmsRowBlock,
    CmsTeaserBlock,
} from "@/gen/realtime";
import { cn } from "@/utils";
import { generateImageUrl } from "@/utils/image";

import { LayoutEditDispatch, LayoutEditState, zSourceData } from "../layoutEditState";
import { teaserImageUrlWithRatio } from "../layoutUtils";
import { AddDialog } from "./AddDialog";

export const ContentEditor = ({
    blocks,
    depth,
    allParents,
    parentId,
    dispatch,
    state,
    row = false,
}: {
    blocks: readonly string[];
    state: Immutable<LayoutEditState>;
    depth: number;
    allParents: string[];
    parentId: string | null;
    dispatch: LayoutEditDispatch;
    row?: boolean;
}) => {
    const validBlocks = blocks.filter(blockId => state.layout.blocks[blockId] != null);

    return (
        <div className={cn("m-2 flex flex-grow basis-1", row ? "flex-row" : "flex-col")}>
            <BetweenRows
                index={-1}
                depth={depth}
                parentId={parentId}
                dispatch={dispatch}
                allParents={allParents}
                row={row}
            />
            {validBlocks.map((block, index) => (
                <motion.div
                    key={block}
                    className={cn(
                        "flex flex-col",
                        row ? "flex-grow flex-row justify-around" : "flex-col justify-stretch"
                    )}
                    layout
                    initial={{ opacity: 0, x: -400, scale: 1 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -400, scale: 1 }}
                    transition={{ duration: 0.6, type: "spring" }}
                >
                    <Block
                        blockId={block}
                        state={state}
                        index={index}
                        depth={depth}
                        parentId={parentId}
                        dispatch={dispatch}
                        allParents={allParents}
                    />
                    <BetweenRows
                        allParents={allParents}
                        index={index}
                        depth={depth}
                        parentId={parentId}
                        dispatch={dispatch}
                        row={row}
                    />
                </motion.div>
            ))}
        </div>
    );
};

const BetweenRows = ({
    index,
    depth,
    parentId,
    dispatch: updateContent,
    allParents,
    row,
}: {
    index: number;
    depth: number;
    parentId: string | null;
    dispatch: LayoutEditDispatch;
    allParents: string[];
    row: boolean;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    useEffect(() => {
        const element = ref.current;
        invariant(element);

        function onChange() {
            setActive(true);
        }

        return combine(
            dropTargetForElements({
                element,
                canDrop: ({ source }) => {
                    const dataRes = zSourceData.safeParse(source.data);

                    if (!dataRes.success) return false;

                    const data = dataRes.data;

                    // Cannot drop in a child of itself
                    if (allParents.includes(data.block.id)) {
                        return false;
                    }

                    return true;
                },
                getData() {
                    return {
                        t: "move",
                        index,
                        depth,
                        parentId,
                    };
                },
                onDragEnter: onChange,
                onDrag: onChange,
                onDragLeave() {
                    setActive(false);
                },
                onDrop() {
                    setActive(false);
                },
            })
        );
    }, [allParents, depth, index, parentId]);

    const onAdd = (block: CmsBlock) => {
        updateContent({
            type: "add-block",
            parentId,
            index: index + 1,
            block,
        });
    };

    if (row) {
        return (
            <div
                ref={ref}
                className={cn(
                    "mx-2 flex flex-grow flex-row items-center justify-center rounded border-dashed",
                    { "border border-black": active }
                )}
            >
                <AddDialog onAdd={onAdd} />
            </div>
        );
    } else {
        return (
            <div ref={ref} className={cn("flex w-full flex-row items-stretch justify-end")}>
                <div
                    className={cn("my-2 flex-grow rounded border border-dashed border-black", {
                        hidden: !active,
                    })}
                ></div>
                <AddDialog onAdd={onAdd} />
            </div>
        );
    }
};

const Block = ({
    blockId,
    state,
    index,
    depth,
    parentId,
    dispatch,
    allParents,
}: {
    blockId: string;
    state: Immutable<LayoutEditState>;
    index: number;
    depth: number;
    parentId: string | null;
    dispatch: LayoutEditDispatch;
    allParents: string[];
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const block = useAtomValue(state.layout.blocks[blockId]);
    const [dragging, setDragging] = useState<boolean>(false);

    const isActive = block.id === state.activeBlock;

    useEffect(() => {
        const dragHandle = ref.current;
        const element = ref.current;
        invariant(element);
        invariant(dragHandle);

        return combine(
            draggable({
                element: dragHandle,
                onDragStart: () => setDragging(true),
                onDrop: () => setDragging(false),
                getInitialData: () => ({ block, index, depth, parentId }),
            })
        );
    }, [block, depth, index, parentId]);

    return (
        <div
            ref={ref}
            className={cn(
                "bg-card text-card-foreground cursor-pointer rounded-lg border p-3 shadow-sm",
                {
                    "border-primary": isActive,
                },
                dragging ? "opacity-50" : ""
            )}
            onClick={ev => {
                ev.stopPropagation();
                dispatch({ type: "set-active-block", blockId });
            }}
        >
            <BlockContent
                state={state}
                dispatch={dispatch}
                block={block}
                depth={depth}
                allParents={allParents}
            />
        </div>
    );
};

const BlockContent = ({
    block,
    depth,
    dispatch,
    state,
    allParents,
}: {
    block: Immutable<CmsBlock>;
    depth: number;
    dispatch: LayoutEditDispatch;
    state: Immutable<LayoutEditState>;
    allParents: string[];
}) => {
    switch (block.kind.t) {
        case "Container":
            return (
                <ContainerBlockContent
                    blockId={block.id}
                    state={state}
                    dispatch={dispatch}
                    block={block.kind.c}
                    depth={depth}
                    allParents={allParents}
                />
            );
        case "Row":
            return (
                <RowBlockContent
                    blockId={block.id}
                    state={state}
                    dispatch={dispatch}
                    block={block.kind.c}
                    depth={depth}
                    allParents={allParents}
                />
            );
        case "Teaser":
            return <TeaserBlockContent block={block.kind.c} />;
        case "LatestContent":
            return (
                <div>
                    <div>{block.kind.c.title}</div>
                    <div className="text-sm">{block.kind.c.subtitle}</div>
                </div>
            );
        case "Column":
            return (
                <ColumnBlockContent
                    blockId={block.id}
                    state={state}
                    dispatch={dispatch}
                    block={block.kind.c}
                    depth={depth}
                    allParents={allParents}
                />
            );
        case "FeaturedStreamingChannel":
            return <FeaturedStreamingChannelBlockContent block={block.kind.c} />;
        default:
            return <>{block.kind.t}</>;
    }
};

const ContainerBlockContent = ({
    blockId,
    block,
    depth,
    dispatch,
    state,
    allParents,
}: {
    blockId: string;
    block: Immutable<CmsContainerBlock>;
    depth: number;
    dispatch: LayoutEditDispatch;
    state: Immutable<LayoutEditState>;
    allParents: string[];
}) => {
    return (
        <div>
            <div>
                <div className="font-bold">{block.title}</div>
                <div>{block.subtitle}</div>
            </div>
            <ContentEditor
                state={state}
                blocks={block.blocks}
                depth={depth + 1}
                parentId={blockId}
                allParents={[...allParents, blockId]}
                dispatch={dispatch}
            />
        </div>
    );
};

const RowBlockContent = ({
    blockId,
    block,
    depth,
    dispatch,
    state,
    allParents,
}: {
    blockId: string;
    block: Immutable<CmsRowBlock>;
    depth: number;
    dispatch: LayoutEditDispatch;
    state: Immutable<LayoutEditState>;
    allParents: string[];
}) => {
    return (
        <div className="relative">
            <div className="absolute left-0 top-0 text-xs font-bold text-gray-400">ROW</div>
            <ContentEditor
                row={block.blocks.length <= 2 ? true : false}
                state={state}
                blocks={block.blocks}
                depth={depth + 1}
                parentId={blockId}
                allParents={[...allParents, blockId]}
                dispatch={dispatch}
            />
        </div>
    );
};

const TeaserBlockContent = ({ block }: { block: CmsTeaserBlock }) => {
    return (
        <div>
            {block.imageId ? (
                <div className="pointer-events-none">
                    <img src={teaserImageUrlWithRatio(block.presentation, block.imageId)} />
                </div>
            ) : null}
            {block.title}
        </div>
    );
};

const ColumnBlockContent = ({
    blockId,
    block,
    depth,
    dispatch,
    state,
    allParents,
}: {
    blockId: string;
    block: Immutable<CmsColumnBlock>;
    depth: number;
    dispatch: LayoutEditDispatch;
    state: Immutable<LayoutEditState>;
    allParents: string[];
}) => {
    return (
        <div className="relative">
            <div className="absolute left-0 top-0 text-xs font-bold text-gray-400">COLUMN</div>
            <ContentEditor
                state={state}
                blocks={block.blocks}
                depth={depth + 1}
                parentId={blockId}
                allParents={[...allParents, blockId]}
                dispatch={dispatch}
            />
        </div>
    );
};

const FeaturedStreamingChannelBlockContent = ({
    block,
}: {
    block: CmsFeaturedStreamingChannelBlock;
}) => {
    return (
        <div className="space-y-2">
            {block.backgroundImageId && (
                <div className="relative">
                    <img
                        src={generateImageUrl({
                            imageId: block.backgroundImageId,
                            width: 200,
                            height: 112,
                            fit: "crop",
                        })}
                        className="w-full rounded"
                        alt="Background"
                    />
                    <div className="absolute inset-0 rounded bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-2 left-2 text-white">
                        <div className="text-sm font-bold">Featured Streaming Channel</div>
                        {block.title && <div className="text-xs">{block.title}</div>}
                    </div>
                </div>
            )}
            {!block.backgroundImageId && (
                <>
                    <div className="font-bold">Featured Streaming Channel</div>
                    {block.title && <div className="text-sm">{block.title}</div>}
                </>
            )}
            {block.streamingChannelId && (
                <div className="text-xs text-gray-500">Channel: {block.streamingChannelId}</div>
            )}
        </div>
    );
};
